import { Fragment, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { FULFILLMENT_LABELS } from '../../utils/orderConstants';
import EmpStatusPill from './EmpStatusPill';
import './OrderLineItemsTable.css';

const CSV_HEADERS = ['Order #', 'Customer', 'Fulfillment', 'Delivery Region', 'Placed Date', 'Delivery Date', 'Status', 'Product', 'Variant', 'Qty', 'Unit Price', 'Subtotal'];

const escCsv = (val) => {
  if (val == null) return '';
  const s = String(val);
  return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
};

const buildCsv = (rows) => {
  const lines = [CSV_HEADERS.join(',')];
  rows.forEach((r) => {
    lines.push([
      escCsv(r.orderNumber),
      escCsv(r.companyName),
      escCsv(r.fulfillmentType),
      escCsv(r.deliveryRegion),
      escCsv(r.placedDate ? formatDate(r.placedDate) : ''),
      escCsv(r.deliveryDate ? formatDate(r.deliveryDate) : ''),
      escCsv(r.status),
      escCsv(r.productName),
      escCsv(r.variantName),
      escCsv(r.quantity),
      escCsv(r.unitPrice),
      escCsv(r.subtotal),
    ].join(','));
  });
  return lines.join('\n');
};

const SORT_PRESETS = [
  { label: 'By Customer', stack: [{ col: 'customer', dir: 'asc' }, { col: 'size', dir: 'desc' }, { col: 'product', dir: 'asc' }] },
  { label: 'By Size',     stack: [{ col: 'size', dir: 'desc' }, { col: 'product', dir: 'asc' }, { col: 'customer', dir: 'asc' }] },
  { label: 'By Region',   stack: [{ col: 'fulfillment', dir: 'asc' }, { col: 'region', dir: 'asc' }, { col: 'customer', dir: 'asc' }, { col: 'size', dir: 'desc' }, { col: 'product', dir: 'asc' }], groupBy: 'fulfillment' },
];

const SORT_KEYS = {
  fulfillment: (r) => (r.fulfillmentType || '').toLowerCase(),
  customer:  (r) => r.companyName.toLowerCase(),
  region:    (r) => (r.deliveryRegion || '').toLowerCase(),
  product:   (r) => r.productName.toLowerCase(),
  size:      (r) => { const m = r.variantName.match(/[\d.]+/); return m ? parseFloat(m[0]) : 99999; },
  qty:       (r) => Number(r.quantity) || 0,
  total:     (r) => Number(r.subtotal) || 0,
  status:    (r) => r.status || '',
};

const FULFILLMENT_LABELS_DISPLAY = {
  delivery: `🚚 ${FULFILLMENT_LABELS.delivery}`,
  pickup:   `📦 ${FULFILLMENT_LABELS.pickup}`,
};

// ── Sort icon ──────────────────────────────────────────────────────────────
const SortIcon = ({ entry, priority, total }) => {
  if (!entry) {
    return <span className="oli-table__sort-icon" aria-hidden="true">↕</span>;
  }
  return (
    <span className="oli-table__sort-icon oli-table__sort-icon--active" aria-hidden="true">
      {entry.dir === 'asc' ? '↑' : '↓'}
      {total > 1 && (
        <span className="oli-table__sort-badge">{priority}</span>
      )}
    </span>
  );
};

SortIcon.propTypes = {
  entry:    PropTypes.shape({ col: PropTypes.string, dir: PropTypes.string }),
  priority: PropTypes.number,
  total:    PropTypes.number.isRequired,
};

SortIcon.defaultProps = {
  entry:    null,
  priority: 0,
};

// ── Sortable header cell ───────────────────────────────────────────────────
const SortableTh = ({ col, label, extraClass, sortStack, onSort }) => {
  const idx      = sortStack.findIndex((s) => s.col === col);
  const entry    = idx !== -1 ? sortStack[idx] : null;
  const ariaSort = entry ? (entry.dir === 'asc' ? 'ascending' : 'descending') : 'none';
  const title    = entry
    ? `Sorted ${entry.dir === 'asc' ? 'A→Z / low→high' : 'Z→A / high→low'} (click to ${entry.dir === 'asc' ? 'reverse' : 'remove'})`
    : `Sort by ${label}`;

  return (
    <th
      className={`oli-table__th oli-table__th--sortable${extraClass ? ` ${extraClass}` : ''}`}
      onClick={() => onSort(col)}
      aria-sort={ariaSort}
      title={title}
    >
      {label}
      <SortIcon entry={entry} priority={idx + 1} total={sortStack.length} />
    </th>
  );
};

SortableTh.propTypes = {
  col:        PropTypes.string.isRequired,
  label:      PropTypes.string.isRequired,
  extraClass: PropTypes.string,
  sortStack:  PropTypes.arrayOf(PropTypes.shape({ col: PropTypes.string, dir: PropTypes.string })).isRequired,
  onSort:     PropTypes.func.isRequired,
};

SortableTh.defaultProps = {
  extraClass: '',
};

/**
 * Flattens orders × items into a single table — one row per product line.
 * Mirrors the spreadsheet view the user shared (Customer, Product, Size, Qty, …).
 * Supports multi-column sort: click to add/toggle/remove each column.
 */
const OrderLineItemsTable = ({ orders = [], loading = false }) => {
  const navigate = useNavigate();
  // Stack of { col, dir } — order defines sort priority
  const [sortStack, setSortStack] = useState(SORT_PRESETS[0].stack);

  const handleSort = useCallback((col) => {
    setSortStack((prev) => {
      const idx = prev.findIndex((s) => s.col === col);
      if (idx === -1) {
        // Not in stack — add as lowest priority, asc
        return [...prev, { col, dir: 'asc' }];
      }
      const entry = prev[idx];
      if (entry.dir === 'asc') {
        // asc → desc
        const next = [...prev];
        next[idx] = { col, dir: 'desc' };
        return next;
      }
      // desc → remove
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  // Flatten: for each order, emit one row per item
  const flatRows = useMemo(() =>
    orders.flatMap((order) =>
      (order.items || []).map((item) => ({
        orderId:         order.id,
        orderNumber:     order.order_number,
        companyName:     order.company_name || '—',
        fulfillmentType: order.fulfillment_type || '',
        deliveryRegion:  order.delivery_region || '',
        placedDate:      order.created_at,
        deliveryDate:    order.delivery_date,
        status:          order.status,
        itemId:       item.id,
        productName:  item.product_variant?.product_name || '—',
        variantName:  item.product_variant?.variant_name || '—',
        quantity:     item.quantity,
        unitPrice:    item.unit_price_dollars,
        subtotal:     item.subtotal_dollars,
        isSample:     item.is_sample || false,
      }))
    ),
    [orders]
  );

  const rows = useMemo(() => {
    if (sortStack.length === 0) return flatRows;
    return [...flatRows].sort((a, b) => {
      for (const { col, dir } of sortStack) {
        const getter = SORT_KEYS[col];
        const av = getter(a);
        const bv = getter(b);
        if (av < bv) return dir === 'asc' ? -1 : 1;
        if (av > bv) return dir === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [flatRows, sortStack]);

  const activePreset = useMemo(() => {
    return SORT_PRESETS.findIndex((p) =>
      p.stack.length === sortStack.length &&
      p.stack.every((s, i) => sortStack[i]?.col === s.col && sortStack[i]?.dir === s.dir)
    );
  }, [sortStack]);

  // When a preset with groupBy is active, compute group header insertion points
  const activeGroupBy = activePreset !== -1 ? SORT_PRESETS[activePreset].groupBy : null;

  const handleExport = useCallback(() => {
    const csv = buildCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const date = new Date().toISOString().slice(0, 10);
    const a = document.createElement('a');
    a.href = url;
    a.download = `line-items-${date}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }, [rows]);

  if (loading) {
    return (
      <div className="oli-table__empty">
        <p className="oli-table__empty-text">Loading…</p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="oli-table__empty">
        <p className="oli-table__empty-label">No Line Items</p>
        <p className="oli-table__empty-sub">No orders with items match the current filters.</p>
      </div>
    );
  }

  return (
    <div className="oli-table-wrap">
      <div className="oli-table__export-bar">
        <div className="oli-table__sort-presets">
          {SORT_PRESETS.map((preset, i) => (
            <button
              key={preset.label}
              type="button"
              className={`oli-table__preset-btn${activePreset === i ? ' oli-table__preset-btn--active' : ''}`}
              onClick={() => setSortStack(preset.stack)}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="oli-table__export-btn"
          onClick={handleExport}
          title="Export visible line items as CSV"
        >
          Export CSV
        </button>
      </div>
      <table className="oli-table">
        <thead className="oli-table__thead">
          <tr>
            <SortableTh col="customer" label="Customer"                              sortStack={sortStack} onSort={handleSort} />
            <SortableTh col="region"   label="Region"  extraClass="oli-table__col--hide-sm oli-table__th--region" sortStack={sortStack} onSort={handleSort} />
            <SortableTh col="product"  label="Product"                               sortStack={sortStack} onSort={handleSort} />
            <SortableTh col="size"     label="Size"   extraClass="oli-table__col--hide-sm oli-table__th--size"   sortStack={sortStack} onSort={handleSort} />
            <SortableTh col="qty"      label="Qty"    extraClass="oli-table__th--num oli-table__th--qty"         sortStack={sortStack} onSort={handleSort} />
            <SortableTh col="total"    label="Total"  extraClass="oli-table__th--num oli-table__th--total"       sortStack={sortStack} onSort={handleSort} />
            <SortableTh col="status"   label="Status" extraClass="oli-table__col--hide-sm oli-table__th--status" sortStack={sortStack} onSort={handleSort} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            // Insert group header when fulfillment type changes
            const groupKey = activeGroupBy === 'fulfillment' ? row.fulfillmentType : null;
            const prevGroupKey = activeGroupBy === 'fulfillment' && idx > 0 ? rows[idx - 1].fulfillmentType : null;
            const showGroupHeader = groupKey !== null && groupKey !== '' && (idx === 0 || groupKey !== prevGroupKey);

            return (
              <Fragment key={`${row.orderId}-${row.itemId}`}>
              {showGroupHeader && (
                <tr className="oli-table__group-header">
                  <td colSpan={7} className="oli-table__group-header-cell">
                    {FULFILLMENT_LABELS_DISPLAY[groupKey] || groupKey}
                  </td>
                </tr>
              )}
            <tr
              className={`oli-table__row${row.isSample ? ' oli-table__row--sample' : ''}`}
              style={{ animationDelay: `${idx * 0.025}s` }}
              onClick={() => navigate(`/employee/orders/${row.orderId}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/employee/orders/${row.orderId}`)}
              aria-label={`${row.companyName} — ${row.productName}, go to order ${row.orderNumber}`}
            >
              <td className="oli-table__td oli-table__td--company">
                <span className="oli-table__company-name">{row.companyName}</span>
                {row.placedDate && (
                  <span className="oli-table__placed-date">
                    {formatDate(row.placedDate)}
                  </span>
                )}
              </td>
              <td className="oli-table__td oli-table__td--region oli-table__col--hide-sm">
                {row.deliveryRegion || '—'}
              </td>
              <td className="oli-table__td oli-table__td--product">
                <span className="oli-table__product-name">
                  {row.productName}
                  {row.isSample && (
                    <span className="oli-table__sample-badge">Sample</span>
                  )}
                </span>
                {/* Mobile-only: variant + status inline under product name */}
                <span className="oli-table__product-meta">
                  <span className="oli-table__variant-inline">{row.variantName}</span>
                  <EmpStatusPill status={row.status} className="emp-status-pill--sm" />
                </span>
              </td>
              <td className="oli-table__td oli-table__td--variant oli-table__col--hide-sm oli-table__td--size">
                {row.variantName}
              </td>
              <td className="oli-table__td oli-table__td--num">
                {row.quantity}
              </td>
              <td className="oli-table__td oli-table__td--num oli-table__td--bold oli-table__td--total">
                {row.isSample
                  ? <span className="oli-table__sample-free">Free</span>
                  : row.subtotal != null ? formatCurrency(row.subtotal) : '—'}
              </td>
              <td className="oli-table__td oli-table__col--hide-sm oli-table__td--status">
                <EmpStatusPill status={row.status} />
              </td>
            </tr>
            </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

OrderLineItemsTable.propTypes = {
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      order_number: PropTypes.string,
      company_name: PropTypes.string,
      fulfillment_type: PropTypes.string,
      created_at: PropTypes.string,
      delivery_date: PropTypes.string,
      status: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          product_variant: PropTypes.shape({
            product_name: PropTypes.string,
            variant_name: PropTypes.string,
          }),
          quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          unit_price_dollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          subtotal_dollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
      ),
    })
  ),
  loading: PropTypes.bool,
};

export default OrderLineItemsTable;
